import { CloseIcon, DownloadIcon } from '@/shared/ui/assets';
import { CustomButton } from '@/shared/ui/components';
import { Typography } from 'antd';
import classNames from 'classnames';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import { useSupportModuleDetails } from './useSupportModuleDetails';
import type { SupportModuleDetailsProps } from './supportModuleDetails.types';
import './supportModuleDetails.scss';

const { Text } = Typography;

/**
 * Renders the detail view of one support module: a close button, the module title truncated with a tooltip, and its PDF embedded in an iframe with a download action. The header ref and download handler come from the details hook, and a login banner class name adjusts the layout when that banner is shown.
 *
 * @param supportModule - Support module supplying the title and the PDF url.
 * @param handleBackClick - Callback invoked by the close button to return to the module list.
 * @returns The support module detail view.
 */
const SupportModuleDetails = ({ supportModule, handleBackClick }: SupportModuleDetailsProps) => {
  const { hasLoginBanner, headerRef, handleDownloadClick } = useSupportModuleDetails({
    supportModule,
  });

  return (
    <div
      ref={headerRef}
      className={classNames('support-module-details', {
        '--has-login-banner': hasLoginBanner,
      })}
    >
      <div className="support-module-details__title">
        <CustomButton
          id="back-btn"
          style="neutral"
          type="secondary"
          size="small"
          icon={<ReactSVG src={CloseIcon} />}
          onClick={handleBackClick}
          ariaLabel={t`Close`}
        />
        <span className="title-text">
          <Text ellipsis={{ tooltip: supportModule.title }}>{supportModule.title}</Text>
        </span>
      </div>
      <div className="support-module-details__content">
        <iframe
          id="pdf-viewer"
          title="PDF file"
          src={encodeURI(supportModule.pdfUrl)}
          width="100%"
          height="100%"
          {...({ type: 'application/pdf' } as any)}
        />
        <CustomButton
          id="download-btn"
          size="small"
          className="download-btn"
          icon={<ReactSVG src={DownloadIcon} />}
          onClick={handleDownloadClick}
        >
          {t`Download`}
        </CustomButton>
      </div>
    </div>
  );
};

export default SupportModuleDetails;
